import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import requestService from '../services/requestService';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorAlert from '../components/ErrorAlert';

// Try to import react-leaflet components, but make them optional
let MapContainer, TileLayer, Marker, Popup;
let isMapAvailable = false;

try {
  const leaflet = require('react-leaflet');
  MapContainer = leaflet.MapContainer;
  TileLayer = leaflet.TileLayer;
  Marker = leaflet.Marker;
  Popup = leaflet.Popup;
  isMapAvailable = true;
} catch (error) {
  console.log('react-leaflet is not available, map functionality will be disabled');
  isMapAvailable = false;
}

function RequestDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Fetch request details using useCallback to stabilize the function reference
  const fetchRequestDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await requestService.getRequestById(id);
      setRequest(response.request);
    } catch (err) {
      console.error('Error fetching request details:', err);
      setError('Failed to load request details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    fetchRequestDetails();
  }, [fetchRequestDetails]);

  // Handle request cancellation

  // Handle request cancellation
  const handleCancelRequest = useCallback(async () => {
    try {
      await requestService.cancelRequest(id);
      navigate('/requests/manage', { state: { message: 'Request successfully cancelled' } });
    } catch (err) {
      console.error('Error cancelling request:', err);
      setError('Failed to cancel request. Please try again.');
    }
  }, [id, navigate]);

  // Format date for display
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  }, []);

  // Determine if the user can cancel this request
  const canCancel = user?.role === 'recipient' && 
    request?.recipient_id === user?.id && 
    request?.status === 'pending';

  // Map position (default to Nairobi if location not available)
  const defaultPosition = [-1.286389, 36.817223]; // Nairobi coordinates
  const mapPosition = request?.location?.lat && request?.location?.lng
    ? [request.location.lat, request.location.lng]
    : defaultPosition;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <ErrorAlert message={error} />
        <div className="mt-4 text-center">
          <Link to="/requests/manage" className="text-primary-600 hover:text-primary-900">
            Back to Requests
          </Link>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Request Not Found</h2>
        <p className="mt-2 text-gray-600">The request you're looking for doesn't exist or you don't have permission to view it.</p>
        <Link to="/requests/manage" className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700">
          Back to Requests
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link to="/requests/manage" className="text-primary-600 hover:text-primary-700">
          &larr; Back to Requests
        </Link>
      </div>
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Request Details
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Request ID: {request.id}
            </p>
          </div>
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
            ${request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
              request.status === 'approved' ? 'bg-green-100 text-green-800' : 
              request.status === 'fulfilled' ? 'bg-blue-100 text-blue-800' :
              request.status === 'canceled' ? 'bg-gray-100 text-gray-800' :
              'bg-red-100 text-red-800'}`}>
            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
          </span>
        </div>
        
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Food Item
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {request.listing_title}
              </dd>
            </div>
            
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Donor
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {request.donor_name}
                {request.donor_organization && ` (${request.donor_organization})`}
              </dd>
            </div>
            
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Requested Quantity
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {request.quantity} {request.quantity_unit}
              </dd>
            </div>
            
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">
                Request Date
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatDate(request.created_at)}
              </dd>
            </div>
            
            {request.pickup_time && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Pickup Time
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {formatDate(request.pickup_time)}
                </dd>
              </div>
            )}
            
            {request.status === 'approved' && !request.pickup_time && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Pickup Instructions
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  Contact the donor to arrange pickup.
                </dd>
              </div>
            )}
            
            {request.notes && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Request Notes
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {request.notes}
                </dd>
              </div>
            )}
            
            {request.location && (request.location.lat || request.location.address) && (
              <div className="bg-gray-50 px-4 py-5 sm:px-6">
                <dt className="text-sm font-medium text-gray-500 mb-2">
                  Pickup Location
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {request.location.address && (
                    <p className="mb-2">{request.location.address}</p>
                  )}
                  
                  {request.location.lat && request.location.lng && isMapAvailable ? (
                    <div className="h-60 rounded-lg overflow-hidden">
                      <MapContainer 
                        center={mapPosition} 
                        zoom={15} 
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        <Marker position={mapPosition}>
                          <Popup>
                            Pickup location for {request.listing_title}
                          </Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  ) : request.location.lat && request.location.lng ? (
                    <div className="h-60 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                      <div className="text-center p-4">
                        <p>Map view is not available</p>
                        <p className="text-sm text-gray-500 mt-2">Coordinates: {request.location.lat}, {request.location.lng}</p>
                        <a 
                          href={`https://www.google.com/maps?q=${request.location.lat},${request.location.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                        >
                          Open in Google Maps
                        </a>
                      </div>
                    </div>
                  ) : null}
                </dd>
              </div>
            )}
          </dl>
        </div>
        
        {/* Actions */}
        <div className="px-4 py-5 sm:px-6 border-t border-gray-200">
          {canCancel ? (
            <button 
              onClick={() => setShowCancelDialog(true)}
              className="w-full sm:w-auto inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700"
            >
              Cancel Request
            </button>
          ) : (
            <div className="text-sm text-gray-500">
              {request.status === 'approved' && (
                <p>This request has been approved. Contact the donor for pickup arrangements.</p>
              )}
              {request.status === 'rejected' && (
                <p>This request was rejected by the donor.</p>
              )}
              {request.status === 'fulfilled' && (
                <p>This request has been fulfilled.</p>
              )}
              {request.status === 'canceled' && (
                <p>You canceled this request.</p>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Cancel dialog */}
      {showCancelDialog && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Cancel Request
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Are you sure you want to cancel this request? This action cannot be undone.
                    </p>
                    <div className="mt-4">
                      <textarea
                        rows="3"
                        placeholder="Optional: Let the donor know why you're cancelling"
                        className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:col-start-2"
                  onClick={handleCancelRequest}
                >
                  Confirm Cancel
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:col-start-1"
                  onClick={() => setShowCancelDialog(false)}
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RequestDetailPage;
